import Observer from "../observer";
import Picker from 'vanilla-picker';
import { routesList } from "../constants";

export class RouteView extends Observer {

    constructor(){
        super();
    }

    init(options) {
        const { rootElement, routeColor, routeId, routeWidth, geoJson } = options;
        this._selectedSearchText = '';
        this._isSearchClean = false;
        this._isSearchInputActive = false;
        this._addElements(rootElement);
        this._renderRouteItems();
        this._initColorPicker(routeColor);
        this._setInitialState({routeId, geoJson, routeWidth, routeColor});
        this._attachEvents();
    }

    onErrorEmit(){
        this._hideDeleteButton();
        this._showErrorMessage();
    }

    hideErrorMessage() {
        this.elements.errorMessage.classList.add('is-hidden');
    }

    _addElements(rootElement){
        this.elements = {
            rootElement: rootElement,
            searchRouteInput: rootElement.querySelector('.js-search-route'),
            deleteRouteBtn: rootElement.querySelector('.js-delete-route'),
            colorPickerHolder: rootElement.querySelector('.js-route-color-picker-holder'),
            colorPickerOpener: rootElement.querySelector('.js-route-color-picker-opener'),
            colorPickerOutput: rootElement.querySelector('.js-route-color-picker-output'),
            routesHolder: rootElement.querySelector('.js-routes-holder'),
            addRouteViaFile: rootElement.querySelector('.js-add-route-input'),
            geojsonTextarea: rootElement.querySelector('.js-geojson-textarea'),
            addRouteViaTextareaBtn: rootElement.querySelector('.js-add-route-btn'),
            inputIcon: rootElement.querySelector('.js-route-input-icon'),
            errorMessage: rootElement.querySelector('.js-geojson-error'),
            inputRange: rootElement.querySelector('.js-route-width-range')
        }
    }

    _renderRouteItems() {
        const routeElementList = this._getRouteElementList();
        this._appendRoutesToDOM(routeElementList);
        this.elements.routeItems = this.elements.rootElement.querySelectorAll('.js-route-item');
    }

    _getRouteElementList () {
        return routesList.routeTypes.map(routeType => {
            const routes = routesList.routes.filter(route => route.routeType === routeType);
            return {
                type: routeType,
                elementList: this._createRoutesElements(routes)
            };
        });
    }

    _createRoutesElements(routes){
        const fragmentElement = document.createDocumentFragment();
        routes.forEach(route => {
            let liElement = document.createElement('li');
            liElement.innerText = route.searchText;
            liElement.setAttribute('data-id', route.id);
            liElement.setAttribute('data-search', route.searchText);
            liElement.classList.add('js-route-item');
            liElement.classList.add('route-item');
            fragmentElement.appendChild(liElement);
        });
        return fragmentElement;
    }

    _appendRoutesToDOM(routesFragments){
        routesFragments.forEach(routeTypeData => {
            const routeTypeSelector = `.js-route-type-${routeTypeData.type}`;
            this.elements.routesHolder.querySelector(routeTypeSelector).append(routeTypeData.elementList);
        });
    }

    _initColorPicker(color) {
        this.picker = new Picker({
            parent: this.elements.colorPickerHolder,
            alpha: false,
            popup: false,
            color
        });
        this.picker.hide();
        this._attachColorPickerEvents();
    }

    _attachColorPickerEvents () {
        this.picker.onChange = (color) => {
            this._setNewRouteColor(color.rgbString);
        };
        this.picker.onDone = () => {
            this.picker.hide();
        }
    }

    _setInitialState({routeId, geoJson, routeWidth, routeColor}) {

        this._setInitialSearchInput(routeId);
        this._setNewRouteColor(routeColor);
        this._setWidthInputValue(routeWidth);

        if(geoJson) {
            this._showDeleteButton();
        }
    }

    _setInitialSearchInput(routeId) {
        const route = routesList.routes.find(route => route.id === routeId);
        if(route) {
            this._selectedSearchText = route.searchText;
            this.elements.searchRouteInput.value = route.searchText;
        }
    }

    _setNewRouteColor(newRouteColor){
        this.elements.colorPickerOutput.style.backgroundColor = newRouteColor;
        this.publish('route:color:update', newRouteColor);
    }

    _setWidthInputValue(routeWidth) {
        this.elements.inputRange.value = routeWidth;
    }

    _attachEvents(){

        this.elements.colorPickerOpener.addEventListener('click', () => {
            this.picker.show();
        });

        this.elements.searchRouteInput.addEventListener('focus', () => {
            this._isSearchInputActive = true;
            this._showList();
        });

        this.elements.searchRouteInput.addEventListener('blur', () => {
            this._isSearchInputActive = false;
            setTimeout(()=>{
                this._hideList();
                this._showActiveRoutes('');
            }, 200);
        });

        document.addEventListener('keyup', (event) => {
            if(this._isSearchInputActive && event.key === 'Escape') {
                this._escapeFromSearch();
            }
        });

        this.elements.searchRouteInput.addEventListener('input', (event) => {
            let value = this._getSearchValue(event);
            this._showActiveRoutes(value)
        });

        this.elements.routeItems.forEach((element) => {
            element.addEventListener('click', (event) => {
                const { routeId, searchText } = this._getRouteData(event);
                this._updateSearchInputState(searchText);
                this._hideList();
                this._showDeleteButton();
                this.publish('route:select:add', routeId);
            });
        });

        this.elements.deleteRouteBtn.addEventListener('click', () => {
            this.publish('route:delete');
            this._hideDeleteButton();
            this._cleanUpInputs();
        });

        this.elements.addRouteViaFile.addEventListener('change', (event) => {
            this._showDeleteButton();
            this.publish('route:file:add', event.target.files[0]);
        });

        this.elements.addRouteViaTextareaBtn.addEventListener('click', () => {
            const geojsonText = this.elements.geojsonTextarea.value.trim();
            this._showDeleteButton();
            this.publish('route:textarea:add', geojsonText);
        });

        this.elements.inputRange.addEventListener('input', (event) => {
            const value = +event.target.value;
            this.publish('route:width:update', value);
        });

        this.elements.geojsonTextarea.addEventListener('input', (event) => {
            const value = event.target.value.trim();
            if(value) {
                this._enableAddRouteViaTextareaBtn();
            } else {
                this._disableAddRouteViaTextareaBtn();
            }

        })

    }

    _getSearchValue(event) {

        let value = event.target.value.trim().toLowerCase();
        if(!this._isSearchClean) {
            value = value[value.length - 1] || '';
            this._isSearchClean = true;
            this.elements.searchRouteInput.value = value;
        }
        return value;
    }

    _getRouteData(event) {
        const routeId = event.target.getAttribute('data-id');
        const searchText = event.target.getAttribute('data-search');
        return {routeId, searchText};
    }

    _updateSearchInputState(searchText) {
        this._isSearchClean = false;
        this.elements.searchRouteInput.value = searchText;
        this._selectedSearchText = searchText;
    }

    _escapeFromSearch() {
        this._isSearchClean = false;
        this.elements.searchRouteInput.value = this._selectedSearchText;
        this.elements.searchRouteInput.blur();
    }

    _showList(){
        this.elements.routesHolder.classList.remove('is-hidden');
        this.elements.inputIcon.classList.remove('icon-arrow_bottom');
        this.elements.inputIcon.classList.add('icon-arrow_top');
    }

    _hideList(){
        this.elements.routesHolder.classList.add('is-hidden');
        this.elements.inputIcon.classList.add('icon-arrow_bottom');
        this.elements.inputIcon.classList.remove('icon-arrow_top');
        this.elements.routesHolder.scrollTop = 0;
    }

    _showDeleteButton(){
        this.elements.deleteRouteBtn.classList.remove('is-hidden');
    }

    _hideDeleteButton(){
        this.elements.deleteRouteBtn.classList.add('is-hidden');
    }

    _showErrorMessage() {
        this.elements.errorMessage.classList.remove('is-hidden');
    }

    _showActiveRoutes(searchRoute){
        this.elements.routeItems.forEach((element) => {
            const elementID = element.getAttribute('data-search');
            if(!elementID.toLowerCase().includes(searchRoute)) {
                element.classList.add('is-hidden')
            } else {
                element.classList.remove('is-hidden')
            }
        });
    }

    _cleanUpInputs(){
        this.elements.searchRouteInput.value = '';
        this.elements.geojsonTextarea.value = '';
        this.elements.addRouteViaFile.value = '';
    }

    _enableAddRouteViaTextareaBtn() {
        this.elements.addRouteViaTextareaBtn.classList.remove('is-disable');
    }

    _disableAddRouteViaTextareaBtn() {
        this.elements.addRouteViaTextareaBtn.classList.add('is-disable');
    }

}